/**
 * Where a session status belongs.
 *
 * One function, so the answer cannot disagree between the route that redirects away and the route
 * that redirects toward. Every auth route's guard is the same two lines: ask this where the current
 * status belongs, and redirect if that is not here.
 *
 * Two entries deserve the note:
 *
 * · **`degraded` goes to the app, not to sign-in.** A degraded session is a session. Sending it to
 *   sign-in would BE the forced-logout bug, expressed at the routing layer instead of in the
 *   reducer — the one place the terminality rule could still be bypassed.
 * · **`restoring` has no destination.** No route may decide anything until the persisted session has
 *   settled (authentication ADR §3), so this returns `null` and the root route's boundary holds the
 *   render instead.
 * · **A signed-out visitor has one home but three legal addresses.** Sign-up and the password reset
 *   are the other surfaces a session-less browser is allowed to be on, so `routeForSessionStatus`
 *   still answers `/sign-in` (that is where a logout lands) while `redirectFrom` leaves a visitor who
 *   deliberately walked to `/sign-up` or `/reset-password` where they are. Without that distinction
 *   those routes are unreachable: their own guards would bounce every visitor they exist for.
 *
 * Pure. Returns a path, not a redirect.
 */

import type { SessionStatus } from './sessionMachine';

/** Every path the session can send a trainer to. Typed so a rename fails `tsc`, not a click. */
export type SessionRoute =
  | '/'
  | '/sign-in'
  | '/sign-up'
  | '/reset-password'
  | '/refused'
  | '/complete-profile';

/**
 * The routes a session-less browser may sit on. Sign-in is the default landing; sign-up and the
 * password reset are reached deliberately and must not be redirected away from.
 */
const SIGNED_OUT_ROUTES: readonly SessionRoute[] = ['/sign-in', '/sign-up', '/reset-password'];

export function routeForSessionStatus(status: SessionStatus): SessionRoute | null {
  switch (status) {
    case 'restoring':
      return null;
    case 'signedIn':
    case 'degraded':
      return '/';
    case 'incomplete':
      return '/complete-profile';
    case 'refused':
      return '/refused';
    case 'signedOut':
      return '/sign-in';
  }
}

/**
 * Where to send a trainer currently on `here`, or `null` to stay. Separate from the mapping above so
 * a guard reads as one question instead of a comparison a caller could invert.
 */
export function redirectFrom(here: SessionRoute, status: SessionStatus): SessionRoute | null {
  const belongs = routeForSessionStatus(status);
  if (belongs === null || belongs === here) return null;
  // The one place a status has more than one legal address — see the header note.
  if (status === 'signedOut' && SIGNED_OUT_ROUTES.includes(here)) return null;
  return belongs;
}
